import { useCallback, useEffect, useState } from 'react';
import PageTemplate from 'components/template/PageTemplate';
import Container from 'components/common/Container';
import { useNavigate } from 'react-router-dom';
import { jsonAxios } from 'components/axios';
import CustomSelect from 'components/common/CustomSelect';
import { quoteStatusCountSelect } from 'data/quote';
import { ITransport, ISearchInfo } from 'types/transport';
import First from 'components/transport/list/First';
import TransportFileUpload from 'components/common/modal/TransportFileUpload';
import { convertQuoteType, countryCodeToEmoji, pointCountry } from 'components/function';
import { convertStatus } from 'data/transport';
import { ReactComponent as Upload } from 'assets/transport/list/upload.svg'
import { ReactComponent as Location } from 'assets/transport/list/location.svg'
import useCheckCompany from 'hook/useCheckCompany';
import { IFileNateon } from 'components/nateon';
import ScoreDrawer from 'components/transport/drawer';
import TransportTracking from 'components/common/modal/TransportTracking';
import Pagination from 'components/common/Pagination';

const TransportList = () => {
    const navigate = useNavigate();
    const [searchInfo, setSearchInfo] = useState<ISearchInfo>({
        fromDate: '',
        toDate: '',
        status: '',
        origin: '',
        destination: '',
        title: '',
        pageSize: 10,
        page: 1,
    });

    const [totalPage, setTotalPage] = useState<number>(0)
    const [totalCount, setTotalCount] = useState<number>(0)
    const [list, setList] = useState<ITransport[]>([]);
    const [popOpen, setPopOpen] = useState<boolean>(false);
    const [popTrackingOpen, setPopTrackingOpen] = useState<boolean>(false);
    const [select, setSelect] = useState<IFileNateon>({} as IFileNateon)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [avgScore, setAvgScore] = useState<number>(0)

    const tHeader = [
        { label: "견적수락일", flex: 1.0 },
        { label: "구분", flex: 0.5 },
        { label: "계정", flex: 1.5 },
        { label: "부킹번호", flex: 1.0 },
        { label: "화물명", flex: 1.0 },
        { label: "출발지", flex: 1.0 },
        { label: "목적지", flex: 1.0 },
        { label: "현재상태", flex: 1.0 },
        { label: "운송수단", flex: 1.0 },
        // ...(user.useSolomon
        //     ? [
        //         { label: "PULSE SCORE", flex: 1.0 },
        //     ]
        //     : []),
        { label: "운송업체", flex: 1.5 },
        { label: "운송현황", flex: 1.0 },
        { label: "서류", flex: 1.0 },
    ];

    const [trackingInfo, setTrackingInfo] = useState({
        type: '',
        bookingNumber: '',
        blNo: '',
        carrierName: '',
    })

    useCheckCompany();

    const init = useCallback(() => {
        const params = new URLSearchParams(Object.entries(searchInfo)).toString();
        jsonAxios.get(`order?${params}`).then(res => {
            if (res.data.code === 200) {
                setList(res.data.data)
                setTotalPage(res.data.totalPages | 0)
                setTotalCount(res.data.totalCount | 0)
                setAvgScore(res.data.avgScore)
            }
        })
    }, [searchInfo])

    useEffect(() => {
        init()
    }, [init])

    const convertTransortType = (type: string) => {
        return type === 'SEA' ? '선박' : type === 'AIR' ? '항공' : '내륙'
    }

    const tBodyStyle = () => {
        return `flex items-center justify-center py-2 text-center tablet:py-0 tablet:gap-2 min-w-0`
    }

    const titleStyle = () => {
        return `hidden tablet:block flex-[1.0] w-full bg-[#F8F8FA] py-1`
    }

    const toggleDrawer = () => {
        setIsOpen((p) => !p)
    }

    return (
        <PageTemplate background='bg-[#F8F8FA]'>
            <Container className="flex flex-col gap-6 tablet:gap-4 mb-6 tablet:py-4 pt-21 h-full min-h-dvh">
                <First
                    searchInfo={searchInfo}
                    setSearchInfo={setSearchInfo}
                    init={init}
                    open={toggleDrawer}
                    avgScore={avgScore}
                />
                <ScoreDrawer
                    isOpen={isOpen}
                    toggleDrawer={toggleDrawer}
                    avgScore={avgScore}
                />

                <div className='flex flex-col flex-1 gap-3'>
                    <div className='bg-white rounded-lg'>
                        <div className='flex tablet:justify-between items-center gap-6 px-10 tablet:px-4 border-b-1 w-full h-20 tablet:h-15'>
                            <p className='font-semibold tablet:text-lg text-2xl'>총 {totalCount}건</p>
                            <div className='w-[148px]'>
                                <CustomSelect
                                    height={40}
                                    fontSize={14}
                                    option={quoteStatusCountSelect}
                                    value={
                                        searchInfo.pageSize
                                            ? quoteStatusCountSelect.find(arr => arr.value === String(searchInfo.pageSize))
                                            : null
                                    }
                                    placeholder={'10개 보기'}
                                    onChange={(value) => {
                                        setSearchInfo(p => ({
                                            ...p,
                                            pageSize: Number(value),
                                            page: 1
                                        }))
                                    }}
                                />
                            </div>
                        </div>
                        <div className='flex flex-col gap-3 px-10 tablet:px-4 py-6 w-full'>
                            <div className='tablet:hidden flex bg-[#F8F8FA] font-semibold text-sm 2xl:text-base'>
                                {
                                    tHeader.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex-[${item.flex}] flex items-center justify-center h-15`}
                                        >
                                            {item.label}
                                        </div>
                                    ))
                                }
                            </div>

                            <div>
                                {
                                    list.length > 0 ?
                                        <div className='flex flex-col gap-3 min-h-[30vh]'>
                                            {
                                                list.map((item, idx) => (
                                                    <div
                                                        className={`flex text-sm border border-[#E1E1E8] transition-all rounded tablet:flex-col tablet:gap-1 ${item.status !== 'CANCELED' ? 'cursor-pointer 2xl:hover:bg-[#EEF4FF]' : ''}`}
                                                        key={idx}
                                                        onClick={() => {
                                                            if (item.status !== 'CANCELED') {
                                                                navigate(`/transport/detail?id=${item.id}`)
                                                            }
                                                        }}
                                                    >
                                                        <div className={`flex-[1.0] ${tBodyStyle()} tablet:order-7`}>
                                                            <p className={titleStyle()}>{tHeader[0].label}</p>
                                                            <p className='flex-1'>
                                                                {item.selectedDate}
                                                            </p>
                                                        </div>
                                                        <div className={`flex-[0.5] ${tBodyStyle()} tablet:order-3`}>
                                                            <p className={titleStyle()}>{tHeader[1].label}</p>
                                                            <p className='flex-1'>
                                                                {convertQuoteType(item.quoteType)}
                                                            </p>
                                                        </div>
                                                        <div className={`flex-[1.5] ${tBodyStyle()} tablet:order-4`}>
                                                            <p className={titleStyle()}>{tHeader[2].label}</p>
                                                            <p className='flex-1 truncate'>
                                                                {item.username}
                                                            </p>
                                                        </div>
                                                        <div className={`flex-[1.0] ${tBodyStyle()} tablet:order-2`}>
                                                            <p className={titleStyle()}>{tHeader[3].label}</p>
                                                            {
                                                                (item.bookingNumber || item.mawb || item.hawb) ?
                                                                    <div className={`flex-[1.0] ${tBodyStyle()} text-primary flex-col`}>
                                                                        {item.bookingNumber && <p>{item.bookingNumber}</p>}
                                                                        {item.mawb && <p>{item.mawb}</p>}
                                                                        {item.hawb && <p>{item.hawb}</p>}
                                                                    </div> :
                                                                    <div className={`flex-[1.0] ${tBodyStyle()}`}>
                                                                        -
                                                                    </div>
                                                            }
                                                        </div>

                                                        <div className={`flex-[1.0] ${tBodyStyle()} tablet:order-5`}>
                                                            <p className={titleStyle()}>{tHeader[4].label}</p>
                                                            <p className='flex-1'>
                                                                {item.productNameKr ? item.productNameKr : '-'}
                                                            </p>
                                                        </div>
                                                        <div className='flex flex-[2.0] tablet:order-1'>
                                                            <div className={`flex-[1.0] ${tBodyStyle()} flex-col tablet:truncate`}>
                                                                <p className={titleStyle()}>{tHeader[5].label}</p>
                                                                <p className='text-lg flag'>{countryCodeToEmoji(item.originCode.slice(0, 2))}</p>
                                                                <p dangerouslySetInnerHTML={{ __html: pointCountry(item.originCode) }}></p>
                                                                <p className='tablet:w-full tablet:truncate'>{item.originAddress}</p>
                                                            </div>
                                                            <div className={`flex-[1.0] ${tBodyStyle()} flex-col tablet:truncate`}>
                                                                <p className={titleStyle()}>{tHeader[6].label}</p>
                                                                <p className='text-lg flag'>{countryCodeToEmoji(item.destinationCode.slice(0, 2))}</p>
                                                                <p dangerouslySetInnerHTML={{ __html: pointCountry(item.destinationCode) }}></p>
                                                                <p className='tablet:w-full tablet:truncate'>{item.destinationAddress}</p>
                                                            </div>
                                                        </div>

                                                        <div className={`flex-[1.0] ${tBodyStyle()} ${item.status === 'CANCELED' && 'text-red'} tablet:order-6`}>
                                                            <p className={titleStyle()}>{tHeader[7].label}</p>
                                                            <p className='flex-1'>
                                                                {convertStatus(item.status)}
                                                            </p>
                                                        </div>
                                                        <div className={`flex-[1.0] ${tBodyStyle()} tablet:order-8`}>
                                                            <p className={titleStyle()}>{tHeader[8].label}</p>
                                                            <p className='flex-1'>
                                                                {convertTransortType(item.transportType)}
                                                            </p>
                                                        </div>
                                                        {/* {
                                                            user.useSolomon &&
                                                            <div className={`flex-[1.0] ${tBodyStyle()} tablet:order-9`}>
                                                                <p className={titleStyle()}>{tHeader[9].label}</p>
                                                                <p className='flex-1'>
                                                                    {item.scoreInfo?.total}
                                                                </p>
                                                            </div>
                                                        } */}
                                                        <div className={`flex-[1.5] ${tBodyStyle()} tablet:order-10`}>
                                                            <p className={titleStyle()}>{tHeader[9].label}</p>
                                                            <p className='flex-1 tablet:truncate'>
                                                                {item.carrierName || '-'}
                                                            </p>
                                                        </div>
                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            className={`flex-[1.0] ${tBodyStyle()} !p-0 tablet:order-11`}>
                                                            <p className={titleStyle()}>{tHeader[10].label}</p>
                                                            <p className='flex flex-1 justify-center items-center'>
                                                                {
                                                                    item.status !== 'CANCELED' ?
                                                                        <Location
                                                                            fill={item.bookingNumber ? '#196BFF' : '#9D9EB7'}
                                                                            onClick={() => {
                                                                                setPopTrackingOpen(true)
                                                                                setSelect({
                                                                                    id: item.id,
                                                                                    carrierName: item.carrierName,
                                                                                    originCode: item.originCode,
                                                                                    originAddress: item.originAddress,
                                                                                    destinationCode: item.destinationCode,
                                                                                    destinationAddress: item.destinationAddress,
                                                                                })
                                                                                setTrackingInfo({
                                                                                    type: item.transportType,
                                                                                    bookingNumber: item.bookingNumber,
                                                                                    blNo: item.blNo,
                                                                                    carrierName: item.carrierName,
                                                                                })
                                                                            }}
                                                                        />
                                                                        : '-'
                                                                }
                                                            </p>
                                                        </div>
                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            className={`flex-[1.0] ${tBodyStyle()} !p-0 tablet:order-12`}>
                                                            <p className={titleStyle()}>{tHeader[11].label}</p>
                                                            <p className='flex flex-1 justify-center items-center'>
                                                                {
                                                                    item.status !== 'CANCELED' ?
                                                                        <Upload
                                                                            fill={(item.bookingNumber || item.mawb || item.hawb) ? '#196BFF' : '#9D9EB7'}
                                                                            onClick={() => {
                                                                                setPopOpen(true)
                                                                                setSelect({
                                                                                    id: item.id,
                                                                                    carrierName: item.carrierName,
                                                                                    originCode: item.originCode,
                                                                                    originAddress: item.originAddress,
                                                                                    destinationCode: item.destinationCode,
                                                                                    destinationAddress: item.destinationAddress,
                                                                                })
                                                                            }}
                                                                        />
                                                                        : '-'
                                                                }
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        :
                                        <div className='flex justify-center items-center py-6 min-h-[30vh]'>
                                            데이터가 없습니다.
                                        </div>
                                }
                            </div>
                        </div>
                    </div>

                    <Pagination
                        totalPage={totalPage}
                        currentPage={searchInfo.page}
                        onClick={(idx) => {
                            setSearchInfo(p => ({
                                ...p,
                                page: (idx + 1)
                            }))
                        }}
                    />
                </div>
            </Container>
            <TransportFileUpload
                isOpen={popOpen}
                setIsOpen={setPopOpen}
                select={select}
                refresh={init}
            />
            <TransportTracking
                isOpen={popTrackingOpen}
                setIsOpen={setPopTrackingOpen}
                info={trackingInfo}
            />
        </PageTemplate>
    )
}

export default TransportList